import type { AudioFeatures, SourceInfo } from "../audio/types";
import {
  createSilentMusicState,
  fromLegacyAudioFeatures,
  toLegacyAudioFeatures,
  type MusicQualityMode,
  type MusicSourceKind,
  type MusicState,
  type ReactivityMode,
} from "./state";

export type MusicRuntimeSurface = "desktop" | "mobile" | "song-galaxy";

export type MusicRuntimeOptions = {
  mode?: ReactivityMode;
  qualityMode?: MusicQualityMode;
  surface?: MusicRuntimeSurface;
  exposeGlobal?: boolean;
};

export type MusicRuntimeUpdateOptions = {
  source?: SourceInfo;
  sourceKind?: MusicSourceKind;
  audioTimeSec?: number;
  sourceTimeSec?: number;
  dtSec?: number;
  confidence?: number;
  label?: string;
};

declare global {
  interface Window {
    __PFX_MUSIC_STATE__?: MusicState;
    __PFX_MUSIC_RUNTIME__?: {
      read: () => MusicState;
      setMode: (mode: ReactivityMode) => void;
    };
  }
}

const DEFAULT_DT_SEC = 1 / 60;

const sourceKindFromInfo = (source?: SourceInfo): MusicSourceKind => source?.kind ?? "none";

export class MusicRuntime {
  private mode: ReactivityMode;
  private state: MusicState;
  private lastFeatureTimeMs = 0;
  private readonly qualityMode: MusicQualityMode;
  private readonly surface: MusicRuntimeSurface;
  private readonly exposeGlobal: boolean;

  constructor(options: MusicRuntimeOptions = {}) {
    this.mode = options.mode ?? "legacy";
    this.qualityMode = options.qualityMode ?? "medium";
    this.surface = options.surface ?? "desktop";
    this.exposeGlobal = Boolean(options.exposeGlobal);
    this.state = createSilentMusicState({ mode: this.mode, qualityMode: this.qualityMode, label: this.surface });
    this.publishGlobal();
  }

  setMode(mode: ReactivityMode): void {
    this.mode = mode;
    this.state = { ...this.state, mode };
    this.publishGlobal();
  }

  read(): MusicState {
    return this.state;
  }

  readLegacyForRenderer(): AudioFeatures {
    return toLegacyAudioFeatures(this.state);
  }

  updateFromLegacy(features: AudioFeatures, options: MusicRuntimeUpdateOptions = {}): MusicState {
    const dtSec = options.dtSec ?? this.computeDtSec(features.t);
    const sourceKind = options.sourceKind ?? sourceKindFromInfo(options.source);
    this.state = fromLegacyAudioFeatures(features, {
      mode: this.mode,
      sourceKind,
      audioTimeSec: options.audioTimeSec,
      sourceTimeSec: options.sourceTimeSec,
      dtSec,
      confidence: options.confidence,
      qualityMode: this.qualityMode,
      label: options.label ?? options.source?.label ?? this.surface,
    });
    this.publishGlobal();
    return this.state;
  }

  private computeDtSec(featureTimeMs: number): number {
    if (this.lastFeatureTimeMs <= 0 || featureTimeMs <= 0) {
      this.lastFeatureTimeMs = featureTimeMs;
      return DEFAULT_DT_SEC;
    }
    const dtSec = Math.max(0, Math.min(0.1, (featureTimeMs - this.lastFeatureTimeMs) / 1000));
    this.lastFeatureTimeMs = featureTimeMs;
    return dtSec || DEFAULT_DT_SEC;
  }

  private publishGlobal(): void {
    if (!this.exposeGlobal || typeof window === "undefined") return;
    window.__PFX_MUSIC_STATE__ = this.state;
    window.__PFX_MUSIC_RUNTIME__ = {
      read: () => this.read(),
      setMode: (mode: ReactivityMode) => this.setMode(mode),
    };
  }
}
